import { Search } from 'lucide-react';
import { useState, type SubmitEventHandler } from 'react';
import { FormField } from '@/components/shared/FormField';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useSearchInFlight, useSetActiveSearch } from '@/lib/active-search';
import {
  BOTH_FIELDS_REQUIRED_MESSAGE,
  canSubmit,
  digitsOnly,
  POLICY_NUMBER_HINT,
  POLICY_NUMBER_MAX_LENGTH,
  POLICY_NUMBER_TOOLTIP,
  validateDateOfBirth,
  validatePolicyNumber,
} from '../utils/policy-search-validation';

/** Ties the guidance above the row to the Policy Number input for assistive technology. */
const POLICY_NUMBER_HINT_ID = 'policy-number-hint';

/**
 * Search panel pinned at the top of the workspace (US-005 AC-001). Field-format errors are live, but
 * the "enter both fields" message waits for the Search button: it appears only when a submit is
 * attempted with a field missing, and clears once both fields are filled.
 */
export function PolicySearchPanel(): React.JSX.Element {
  const [policyNumber, setPolicyNumber] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [missingFieldsOnSubmit, setMissingFieldsOnSubmit] = useState(false);
  const setActiveSearch = useSetActiveSearch();
  const inFlight = useSearchInFlight();

  const policyNumberError = validatePolicyNumber(policyNumber);
  const dateOfBirthError = validateDateOfBirth(dateOfBirth);
  const ready = canSubmit(policyNumber, dateOfBirth);

  const bothPresent = policyNumber.trim().length > 0 && dateOfBirth.trim().length > 0;

  // Editing a field withdraws the last submit's verdict. Without this, filling both fields and then
  // clearing one would resurface the message mid-typing — the exact nagging the on-submit design removed.
  const editField = (set: (value: string) => void) => (value: string) => {
    setMissingFieldsOnSubmit(false);
    set(value);
  };

  const handleSubmit: SubmitEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    // The button stays clickable with a field missing so this submit can happen: the required-fields
    // message is shown in answer to the click, not while the provider is still typing.
    if (!bothPresent) {
      setMissingFieldsOnSubmit(true);
      return;
    }
    setMissingFieldsOnSubmit(false);

    // Re-checked here and not left to the button: the handler owns the invariant, so it holds for any
    // submit that reaches it rather than only for the one route the disabled attribute happens to close.
    if (!ready || inFlight) {
      return;
    }

    setActiveSearch({ policyNumber: policyNumber.trim(), dateOfBirth });
  };

  return (
    <section className="border-line rounded-xl border bg-surface p-6 shadow-sm">
      <SearchIntro />

      {/* Above the fields rather than inside the Policy Number cell (AC-3). Inside it, this block grew
          that one grid cell and pushed its input out of line with Date of Birth and the button. */}
      <p id={POLICY_NUMBER_HINT_ID} className="text-muted mt-4 text-xs leading-relaxed">
        {POLICY_NUMBER_HINT}
      </p>

      <form
        onSubmit={handleSubmit}
        // The browser's own required-field bubble would swallow the click before handleSubmit ran; the
        // portal's message (below) is the one PP-004 wants, so native validation is turned off.
        noValidate
        aria-label="Policy search"
        className="mt-2 grid gap-4 md:grid-cols-[1fr_1fr_auto] md:items-start"
      >
        <PolicyNumberField
          value={policyNumber}
          error={policyNumberError}
          onChange={editField(setPolicyNumber)}
        />
        <DateOfBirthField
          value={dateOfBirth}
          error={dateOfBirthError}
          onChange={editField(setDateOfBirth)}
        />

        <SearchButton inFlight={inFlight} />
      </form>

      <MissingFieldsAlert show={missingFieldsOnSubmit && !bothPresent} />
    </section>
  );
}

/**
 * Answer to a Search click with a field missing (PP-004). Rendered only then — never while the provider
 * is still typing — and it clears itself once both fields are filled.
 */
function MissingFieldsAlert({ show }: { readonly show: boolean }): React.JSX.Element | null {
  if (!show) {
    return null;
  }

  return (
    <p role="alert" className="text-destructive mt-3 text-sm">
      {BOTH_FIELDS_REQUIRED_MESSAGE}
    </p>
  );
}

/**
 * Blocked while the search it submitted is still resolving, so a provider cannot fire a second pair of
 * requests over a half-drawn page. It stays clickable when a field is missing or malformed — the click
 * is what surfaces the required-fields message, and the submit handler refuses the search itself. The
 * label keeps the word "Search" in both states — the accessible name is what tests and assistive
 * technology find the control by.
 */
function SearchButton({ inFlight }: { readonly inFlight: boolean }): React.JSX.Element {
  return (
    <Button
      type="submit"
      disabled={inFlight}
      // min-w rather than w: "Searching…" plus the spinner and the padding overflows a fixed 8rem.
      className="mt-6 h-10 gap-2 whitespace-nowrap md:min-w-32"
    >
      {inFlight ? <Spinner /> : <Search aria-hidden="true" className="h-4 w-4" />}
      {inFlight ? 'Searching…' : 'Search'}
    </Button>
  );
}

function SearchIntro(): React.JSX.Element {
  return (
    <>
      <h1 className="text-brand-600 text-xl font-bold">Search</h1>
      <p className="text-muted mt-1 text-sm">
        To view claims status and to verify coverage of a policy holder, enter the policy number and
        date of birth below.
      </p>
    </>
  );
}

interface FieldProps {
  readonly value: string;
  readonly error: string | null;
  onChange: (value: string) => void;
}

function PolicyNumberField({ value, error, onChange }: FieldProps): React.JSX.Element {
  return (
    <FormField
      label="Policy Number"
      name="policyNumber"
      value={value}
      // The cap is applied AFTER the separators are stripped, and `maxLength` is deliberately absent.
      // The browser enforces maxLength on the raw value, so pasting "999 123 4567 890" would be cut to
      // 13 characters first and then reduced to "9991234567" — a valid number for a different policy.
      onChange={(event): void => {
        onChange(digitsOnly(event.target.value).slice(0, POLICY_NUMBER_MAX_LENGTH));
      }}
      required
      inputMode="numeric"
      autoComplete="off"
      placeholder="Policy Number"
      describedById={POLICY_NUMBER_HINT_ID}
      tooltip={POLICY_NUMBER_TOOLTIP}
      error={error}
    />
  );
}

function DateOfBirthField({ value, error, onChange }: FieldProps): React.JSX.Element {
  return (
    <FormField
      label="Date of Birth"
      name="dateOfBirth"
      type="date"
      value={value}
      onDateChange={onChange}
      required
      error={error}
    />
  );
}
